import React, { Suspense, lazy } from 'react';
import NotificationManager from 'components/common/notifications-manager';
import Localization from 'components/localization';
import AntdWrapper from 'components/antd-wrapper';
import { CacheProvider } from '@emotion/react';
import { Provider } from 'mobx-react';
import createCache from '@emotion/cache';
import history from 'services/history-service';
import useStores from 'hooks/use-stores.hooks';
import useApiClient from 'hooks/use-api-client';
import Router from 'components/routes/router';
import {
  QueryClientProvider,
  QueryClientProvider as QueryClientProviderNext
} from 'stores/rq/common/query-client-provider';
import { KBarProvider } from 'kbar';
import GlobalSubscriptions from 'components/common/global-subscriptions';
import { AppSwitch } from 'components/app-route-switch';
import ErrorBoundary from 'components/common/error-boundary';
import PayGate from 'components/common/pay-gate';
import SentryEventProcessors from 'components/sentry/sentry-event-processors';
import ThemeProviderWrapper from 'components/theme-provider-wrapper';
import ContentSecurityPolicyStyleInjector from 'components/common/content-security-policy-style-injector';
import { areDevTogglesEnabled } from 'constants/config';

const DevToggles = areDevTogglesEnabled ? lazy(() => import('components/common/dev-toggles')) : null;

const emotionCache = createCache({
  key: 'css',
  nonce: window.nonce
});

emotionCache.compat = true;

const App: React.FunctionComponent = () => {
  const stores = useStores();
  const apiClient = useApiClient();

  return (
    <CacheProvider value={emotionCache}>
      <ErrorBoundary renderErrorMessageOnError={false}>
        <ContentSecurityPolicyStyleInjector />
        <Provider {...{ ...stores, apiClient }}>
          <ThemeProviderWrapper>
            <Localization>
              <AntdWrapper>
                <Router history={history}>
                  <QueryClientProviderNext>
                    <QueryClientProvider>
                      <GlobalSubscriptions>
                        <KBarProvider options={{ enableHistory: true }}>
                          <NotificationManager />
                          <PayGate />
                          <AppSwitch />
                          <SentryEventProcessors />
                          {DevToggles ? (
                            <Suspense fallback={null}>
                              <DevToggles />
                            </Suspense>
                          ) : null}
                        </KBarProvider>
                      </GlobalSubscriptions>
                    </QueryClientProvider>
                  </QueryClientProviderNext>
                </Router>
              </AntdWrapper>
            </Localization>
          </ThemeProviderWrapper>
        </Provider>
      </ErrorBoundary>
    </CacheProvider>
  );
};

export default App;
